import React from 'react';
import SevereColdSharp from '@mui/icons-material/SevereColdSharp';

import { Badge } from 'src/component/ds/ui/badge';
import { Card, CardContent, CardHeader } from 'src/component/ds/ui/card';
import { Skeleton } from 'src/component/ds/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/component/ds/ui/table';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import { FORECAST_DAY_COUNT } from './AmbientTemperature/ambientTemperature';
import useAmbientTemperature from './AmbientTemperature/useAmbientTemperature';

const CELL = 'ds-px-2 ds-py-[7px]';
const HEAD = 'ds-py-[7.5px]';
const DATE_CELL = 'ds-w-[106px]';

/* A badge marks any temperature at or below freezing, high or low alike. */
function TemperatureCell ({ value, freezing, bold }: { value: string; freezing: boolean; bold: boolean }) {
  if (!freezing) return <TableCell className={cn(CELL, bold ? 'ds-font-bold' : 'ds-font-normal')}>{value}</TableCell>;

  return (
    <TableCell className='ds-px-2 ds-py-0.5'>
      <Badge variant='secondary' data-testid='ambient-temperature-freezing'>
        {bold ? <span className='ds-font-bold'>{value}</span> : value}
      </Badge>
    </TableCell>
  );
}

export default function AmbientTemperature ({ className }: { className?: string }) {
  const { t, tPlural } = useTranslation();
  const { hasForecast, days, freezingDayCount, freezingLabel, loading } = useAmbientTemperature();

  if (!loading && !hasForecast) return null;

  const skeletonRows = Array.from({ length: FORECAST_DAY_COUNT }, (_, index) => index);

  return (
    <Card
      className={cn('ds-flex ds-h-[324px] ds-w-[278px] ds-flex-col ds-shadow-sm', className)}
      data-testid='ambient-temperature'
    >
      <CardHeader className='ds-p-4'>
        <h3 className='ds-text-base ds-font-semibold ds-text-foreground'>
          {t('dashboard.ambientTemperature')}
        </h3>
      </CardHeader>

      <div className='ds-h-8 ds-shrink-0 ds-px-4 ds-py-1'>
        {loading && <Skeleton className='ds-h-6 ds-w-[212px]' data-testid='ambient-temperature-summary-skeleton' />}
        {!loading && freezingDayCount > 0 && (
          <Badge variant='secondary' data-testid='ambient-temperature-freezing-summary'>
            <SevereColdSharp fontSize='inherit' aria-hidden='true' />
            {tPlural('dashboard.daysBelowFreezing', freezingDayCount, { temp: freezingLabel })}
          </Badge>
        )}
        {!loading && freezingDayCount === 0 && (
          <Badge variant='outline'data-testid='ambient-temperature-no-freezing'>
            {t('dashboard.noDaysBelowFreezing', { temp: freezingLabel })}
          </Badge>
        )}
      </div>

      <CardContent className='ds-min-h-0 ds-flex-1 ds-overflow-hidden ds-p-4 ds-pt-4'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={cn(HEAD, DATE_CELL)}>{t('dashboard.ambientTemperatureDate')}</TableHead>
              <TableHead className={HEAD}>{t('dashboard.ambientTemperatureHigh')}</TableHead>
              <TableHead className={HEAD}>{t('dashboard.ambientTemperatureLow')}</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading
              ? skeletonRows.map((row) => (
                <TableRow key={row}>
                  <TableCell className={cn(CELL, DATE_CELL)}>
                    <Skeleton className='ds-h-4 ds-w-[70px]' data-testid='ambient-temperature-skeleton' />
                  </TableCell>
                  <TableCell className={CELL}><Skeleton className='ds-h-4 ds-w-6' /></TableCell>
                  <TableCell className={CELL}><Skeleton className='ds-h-4 ds-w-6' /></TableCell>
                </TableRow>
              ))
              : days.map((day, index) => {
                const isToday = index === 0;
                const weight = isToday ? 'ds-font-bold' : 'ds-font-normal';

                return (
                  <TableRow key={day.key} data-testid='ambient-temperature-row'>
                    <TableCell className={cn(CELL, DATE_CELL, weight)}>{day.label}</TableCell>
                    <TemperatureCell value={day.high} freezing={day.isHighFreezing} bold={isToday} />
                    <TemperatureCell value={day.low} freezing={day.isLowFreezing} bold={isToday} />
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
